import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

const Testimonials = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showTestimonyModal, setShowTestimonyModal] = useState(false);
  const [selectedTestimony, setSelectedTestimony] = useState(null);
  const [testimonies, setTestimonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch testimonies from API
  useEffect(() => {
    const fetchTestimonies = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_BASE}/testimonials/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': '69420',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch testimonials: ${response.status}`);
        }
        
        const data = await response.json();
        
        console.log('Testimonials API response:', data);
        
        // Handle different response formats
        if (Array.isArray(data)) {
          // Filter only approved testimonies
          setTestimonies(data.filter(t => t.is_approved));
        } else if (data && Array.isArray(data.results)) {
          // Paginated response
          setTestimonies(data.results.filter(t => t.is_approved));
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
        setTestimonies([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTestimonies();
  }, []);

  // Helper to extract plain text from Quill Delta JSON
  const extractMessageText = (messageJson) => {
    try {
      const parsed = JSON.parse(messageJson);
      return parsed.ops
        .map(op => op.insert)
        .join('')
        .replace(/\n+/g, ' ')
        .trim();
    } catch {
      return '';
    }
  };

  const filteredTestimonies = testimonies.filter(testimony => {
    const message = extractMessageText(testimony.messageJson);
    const matchesSearch = testimony.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const openTestimonyModal = (testimony) => {
    setSelectedTestimony(testimony);
    setShowTestimonyModal(true);
  };

  const closeTestimonyModal = () => {
    setShowTestimonyModal(false);
    setSelectedTestimony(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (showTestimonyModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTestimonyModal]);

  if (loading) {
    return (
      <>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}>
          <div style={{ 
            width: '44px', 
            height: '44px', 
            border: '4px solid #e8f4fd', 
            borderTopColor: '#0b1c6b', 
            borderRadius: '50%', 
            animation: 'spin 0.8s linear infinite' 
          }}></div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
        <div className="container" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>⚠️</div>
          <h4 style={{ fontSize: '1.1rem', color: '#0b1c6b', marginBottom: '8px' }}>
            Could not load testimonials
          </h4>
          <p style={{ color: '#6b7280' }}>{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="testimonials-page">
        {/* HERO SECTION */}
        <section className="testimonials-hero">
          <div className="hero-overlay">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 text-white mb-4 mb-lg-0">
                  <h1 className="display-3 fw-bold mb-3">Testimonies</h1>
                  <p className="fs-5 mb-4">How God is demonstrating His working power fixing issues in people's lives.</p>
                  <p className="lead mb-0">
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="search-container">
                    <div className="input-group input-group-lg shadow-lg">
                      <span className="input-group-text bg-white border-0">
                        <i className="bi bi-search text-primary fs-5"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-0"
                        placeholder="Search testimonies by name or keyword..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RESULTS COUNT */}
        <section className="py-4 bg-white border-bottom">
          <div className="container">
            <p className="text-muted mb-0">
              <i className="bi bi-chat-heart me-2"></i>
              Showing {filteredTestimonies.length} testimon{filteredTestimonies.length !== 1 ? 'ies' : 'y'}
            </p>
          </div>
        </section>

        {/* TESTIMONIES GRID */}
        <section className="py-5 bg-light">
          <div className="container">
            {filteredTestimonies.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                <h5 className="text-muted">No testimonies found</h5>
                <p className="text-muted">Try adjusting your search</p>
              </div>
            ) : (
              <div className="row g-4">
                {filteredTestimonies.map((testimony) => {
                  const message = extractMessageText(testimony.messageJson);
                  return (
                    <div className="col-lg-4 col-md-6" key={testimony.id}>
                      <div 
                        className="card border-0 shadow-sm rounded-4 h-100 testimony-card"
                        onClick={() => openTestimonyModal(testimony)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-body p-4">
                          {/* User Info */}
                          <div className="d-flex align-items-center mb-3">
                            {testimony.picture ? (
                              <img 
                                src={testimony.picture} 
                                alt={testimony.name}
                                className="rounded-circle me-3"
                                style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div 
                                className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                                style={{ width: '60px', height: '60px' }}
                              >
                                <i className="bi bi-person-fill fs-3 text-primary"></i>
                              </div>
                            )}
                            <div>
                              <h6 className="fw-bold mb-1 text-primary">{testimony.name}</h6>
                              <p className="text-muted small mb-0">
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDate(testimony.date)}
                              </p>
                            </div>
                          </div>

                          {/* Message Preview */}
                          <p className="text-muted mb-3" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                            <i className="bi bi-quote text-primary fs-4"></i>
                            {message.slice(0, 150)}...
                          </p>

                          {/* Read More */}
                          <button className="btn btn-outline-primary btn-sm rounded-pill w-100">
                            <i className="bi bi-book me-2"></i>Read Full Testimony
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* TESTIMONY DETAILS MODAL */}
        {showTestimonyModal && selectedTestimony && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-primary">
                    <i className="bi bi-chat-heart me-2"></i>
                    Testimony
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={closeTestimonyModal}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  {/* User Info */}
                  <div className="d-flex align-items-center mb-4">
                    {selectedTestimony.picture ? (
                      <img 
                        src={selectedTestimony.picture} 
                        alt={selectedTestimony.name}
                        className="rounded-circle me-3"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                        style={{ width: '80px', height: '80px' }}
                      >
                        <i className="bi bi-person-fill fs-1 text-primary"></i>
                      </div>
                    )}
                    <div>
                      <h4 className="fw-bold mb-1 text-primary">{selectedTestimony.name}</h4>
                      <p className="text-muted mb-0">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(selectedTestimony.date)}
                        {selectedTestimony.time && (
                          <>
                            <span className="mx-2">•</span>
                            <i className="bi bi-clock me-1"></i>
                            {formatTime(selectedTestimony.time)}
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Full Message */}
                  <div className="p-4 bg-light rounded-4">
                    <i className="bi bi-quote text-primary fs-1 d-block mb-3"></i>
                    <p className="text-muted lh-lg mb-0" style={{ fontSize: '16px' }}>
                      {extractMessageText(selectedTestimony.messageJson)}
                    </p>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button className="btn btn-outline-secondary rounded-pill px-4" onClick={closeTestimonyModal}>
                    Close
                  </button>
                  <button className="btn btn-primary rounded-pill px-4">
                    <i className="bi bi-share me-2"></i>Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: '600px' }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/company/gospel-city-church-international/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');

          * {
            font-family: 'Lexend', sans-serif;
          }

          /* HERO */
          .testimonials-hero {
            position: relative;
            background-image: url('/images/choir.png');
            background-size: cover;
            background-position: center;
            min-height: 500px;
            display: flex;
            align-items: center;
          }

          .hero-overlay {
            position: relative;
            width: 100%;
            z-index: 2;
            padding: 80px 0;
          }

          .testimonials-hero::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(135deg, rgba(11,28,107,0.95), rgba(21,158,236,0.85));
            z-index: 1;
          }

          .search-container .form-control {
            padding: 14px 20px;
            font-size: 16px;
          }

          .search-container .form-control:focus {
            box-shadow: 0 0 0 0.25rem rgba(26,58,161,0.25);
          }

          .search-container .input-group-text {
            padding: 14px 20px;
          }

          /* TESTIMONY CARDS */
          .testimony-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .testimony-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 50px rgba(26,58,161,0.2) !important;
          }

          /* MODAL */
          .modal {
            animation: fadeIn 0.3s ease;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* COLORS */
          .text-primary {
            color: #1a3aa1 !important;
          }

          .text-info {
            color: #159EEC !important;
          }

          .bg-info {
            background-color: #159EEC !important;
          }

          .bg-primary {
            background-color: #0b1c6b !important;
          }

          .btn-primary {
            background: #0b1c6b;
            border-color: #0b1c6b;
          }

          .btn-primary:hover {
            background: #159EEC;
            border-color: #159EEC;
          }

          .btn-outline-primary {
            color: #0b1c6b;
            border-color: #0b1c6b;
          }

          .btn-outline-primary:hover {
            background: #0b1c6b;
            border-color: #0b1c6b;
            color: #fff;
          }

          /* SOCIAL */
          .social-section {
            background: linear-gradient(135deg,#0b1c6b,#159EEC);
          }

          .social-icon {
            width: 52px;
            height: 52px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s;
          }

          .social-icon:hover {
            background: rgba(255,255,255,0.3);
            transform: translateY(-4px) scale(1.1);
          }

          @media (max-width: 768px) {
            .testimonials-hero {
              min-height: 400px;
            }

            .display-3 {
              font-size: 2.5rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default Testimonials;